import {
  useCreate,
  useDelete,
  useLocaleState,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useEffect, useId, useState, type FormEvent } from "react";

import { platformLabelKey } from "@/components/competitors/platforms";
import { ConfirmDeleteButton, FormActions } from "@/components/competitors/ui";
import {
  Field,
  FieldError,
  FormDialog,
  SELECT_CLASS,
} from "@/components/market-intel/FormDialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { formatDate } from "@/lib/dates";
import { useIsMobile } from "@/hooks/use-mobile";

import { fromLocalInput } from "./calendar";
import {
  CONTENT_FORMATS,
  CONTENT_STATUSES,
  CONTENT_VERTICALS,
  formatLabelKey,
  statusLabelKey,
  verticalLabelKey,
} from "./constants";
import { useMarketing } from "./context";
import {
  normalizePieceForm,
  pieceFormDefaults,
  type PieceFormValues,
} from "./forms";
import { findById, saleName } from "./hooks";
import { nextIndex } from "./pipeline";
import { phaseOfWeek, planWeekOf, sortPhases, sortPillars } from "./plans";
import type { ContentItem, ContentStatus } from "./types";

export const PieceForm = ({
  open,
  onOpenChange,
  piece,
  prefill,
  onDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edits this piece; creates one when null. */
  piece: ContentItem | null;
  prefill?: Partial<PieceFormValues>;
  onDeleted?: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "es"] = useLocaleState();
  const isMobile = useIsMobile();
  const formId = useId();
  const { data, sales } = useMarketing();
  const [values, setValues] = useState(() => pieceFormDefaults(piece, prefill));
  const [error, setError] = useState<string | null>(null);
  const [create, { isPending: creating }] = useCreate();
  const [update, { isPending: updating }] = useUpdate();
  const [remove, { isPending: deleting }] = useDelete();

  useEffect(() => {
    if (open) {
      setValues(pieceFormDefaults(piece, prefill));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, piece]);

  const plan = findById(data.plans, values.plan_id);
  const pillars = sortPillars(
    data.pillars.filter((pillar) => String(pillar.plan_id) === values.plan_id),
  );
  const phases = sortPhases(
    data.phases.filter((phase) => String(phase.plan_id) === values.plan_id),
  );

  /** With a plan and a date, fill the plan week and phase when empty. */
  const withPlanWeek = (next: PieceFormValues): PieceFormValues => {
    const nextPlan = findById(data.plans, next.plan_id);
    const iso = fromLocalInput(next.scheduled_at);
    if (!nextPlan || !iso) return next;
    const week = planWeekOf(nextPlan, iso.slice(0, 10)) ?? null;
    if (week == null || week < 1) return next;
    const filled = next.week_number
      ? next
      : { ...next, week_number: String(week) };
    const phase = phaseOfWeek(
      data.phases.filter((entry) => String(entry.plan_id) === next.plan_id),
      Number(filled.week_number),
    );
    return filled.phase_id || !phase
      ? filled
      : { ...filled, phase_id: String(phase.id) };
  };

  const set =
    <K extends keyof PieceFormValues>(key: K) =>
    (value: PieceFormValues[K]) => {
      setValues((current) => {
        let next = { ...current, [key]: value };
        if (key === "plan_id")
          next = {
            ...next,
            phase_id: "",
            pillar_id: "",
            secondary_pillar_id: "",
            week_number: "",
          };
        return key === "plan_id" || key === "scheduled_at"
          ? withPlanWeek(next)
          : next;
      });
      setError(null);
    };
  const close = () => onOpenChange(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizePieceForm(values);
    if (!result.ok) return setError(result.error);
    const payload = { ...result.data } as Partial<ContentItem>;
    if (payload.status === "publicado" && !piece?.published_at)
      payload.published_at = payload.scheduled_at ?? new Date().toISOString();
    if (payload.status !== "publicado" && piece?.published_at)
      payload.published_at = null;
    const options = {
      onSuccess: () => {
        notify("marketing.form.saved", { type: "success" });
        close();
      },
      onError: () => notify("marketing.form.error", { type: "error" }),
    };
    if (piece) {
      if (payload.status !== piece.status)
        payload.index = nextIndex(data.items, payload.status as ContentStatus);
      update(
        "content_items",
        { id: piece.id, data: payload, previousData: piece },
        options,
      );
    } else
      create(
        "content_items",
        {
          data: {
            ...payload,
            index: nextIndex(data.items, payload.status as ContentStatus),
          },
        },
        options,
      );
  };

  const onDelete = () => {
    if (!piece) return;
    remove(
      "content_items",
      { id: piece.id, previousData: piece },
      {
        onSuccess: () => {
          notify("marketing.piece.deleted", { type: "info" });
          close();
          onDeleted?.();
        },
        onError: () => notify("marketing.form.error", { type: "error" }),
      },
    );
  };

  const iso = fromLocalInput(values.scheduled_at);
  const weekday = iso
    ? formatDate(iso, locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  const errorId = `${formId}-error`;
  const field = (name: string) => `${formId}-${name}`;
  const select = (
    name: keyof PieceFormValues,
    label: string,
    options: { value: string; label: string }[],
    empty?: string,
  ) => (
    <Field id={field(name)} label={label}>
      <select
        id={field(name)}
        className={SELECT_CLASS}
        value={values[name]}
        onChange={(event) =>
          set(name)(event.target.value as PieceFormValues[typeof name])
        }
      >
        {empty != null ? <option value="">{empty}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate(piece ? "marketing.piece.edit" : "marketing.piece.new")}
      formId={formId}
      onSubmit={submit}
      actions={
        <>
          {piece ? (
            <ConfirmDeleteButton
              label={translate("marketing.piece.delete")}
              onConfirm={onDelete}
              disabled={deleting}
              className="mr-auto"
            />
          ) : null}
          <FormActions
            formId={formId}
            disabled={creating || updating}
            onCancel={close}
          />
        </>
      }
    >
      <Field id={field("title")} label={translate("marketing.form.title")}>
        <Input
          id={field("title")}
          value={values.title}
          onChange={(event) => set("title")(event.target.value)}
          aria-invalid={error === "marketing.form.title_required" || undefined}
          aria-describedby={error ? errorId : undefined}
          autoFocus={!isMobile && !piece}
          maxLength={240}
        />
      </Field>
      {error ? <FieldError id={errorId}>{translate(error)}</FieldError> : null}
      <div className="grid grid-cols-2 gap-3">
        {select(
          "channel_id",
          translate("marketing.piece.channel"),
          data.channels.map((channel) => ({
            value: String(channel.id),
            label: `${channel.name} · ${translate(platformLabelKey(channel.platform))}`,
          })),
          translate("marketing.common.no_channel"),
        )}
        {select(
          "format",
          translate("marketing.format.label"),
          CONTENT_FORMATS.map((format) => ({
            value: format,
            label: translate(formatLabelKey(format)),
          })),
        )}
        {select(
          "status",
          translate("marketing.status.label"),
          CONTENT_STATUSES.map((status) => ({
            value: status,
            label: translate(statusLabelKey(status)),
          })),
        )}
        {select(
          "vertical",
          translate("marketing.vertical.label"),
          CONTENT_VERTICALS.map((vertical) => ({
            value: vertical,
            label: translate(verticalLabelKey(vertical)),
          })),
        )}
      </div>
      <Field
        id={field("scheduled_at")}
        label={translate("marketing.form.scheduled_at")}
      >
        <Input
          id={field("scheduled_at")}
          type="datetime-local"
          value={values.scheduled_at}
          onChange={(event) => set("scheduled_at")(event.target.value)}
        />
        {weekday ? (
          <p className="text-xs text-muted-foreground first-letter:uppercase">
            {weekday}
          </p>
        ) : null}
      </Field>
      {select(
        "plan_id",
        translate("marketing.piece.plan"),
        data.plans.map((entry) => ({
          value: String(entry.id),
          label: entry.name,
        })),
        translate("marketing.common.no_plan"),
      )}
      {plan ? (
        <div className="grid grid-cols-2 gap-3">
          {select(
            "phase_id",
            translate("marketing.piece.phase"),
            phases.map((phase) => ({
              value: String(phase.id),
              label: phase.name
                ? `${phase.number} · ${phase.name}`
                : translate("marketing.plans.phase_name", {
                    number: phase.number,
                  }),
            })),
            translate("marketing.common.none"),
          )}
          <Field id={field("week")} label={translate("marketing.piece.week")}>
            <Input
              id={field("week")}
              inputMode="numeric"
              value={values.week_number}
              onChange={(event) => set("week_number")(event.target.value)}
              aria-invalid={
                error === "marketing.form.week_invalid" || undefined
              }
            />
          </Field>
          {select(
            "pillar_id",
            translate("marketing.piece.pillar"),
            pillars.map((pillar) => ({
              value: String(pillar.id),
              label: `${pillar.code} · ${pillar.name}`,
            })),
            translate("marketing.common.none"),
          )}
          {select(
            "secondary_pillar_id",
            translate("marketing.form.secondary_pillar"),
            pillars
              .filter((pillar) => String(pillar.id) !== values.pillar_id)
              .map((pillar) => ({
                value: String(pillar.id),
                label: `${pillar.code} · ${pillar.name}`,
              })),
            translate("marketing.common.none"),
          )}
        </div>
      ) : null}
      {select(
        "assignee_id",
        translate("marketing.piece.assignee"),
        sales.map((sale) => ({
          value: String(sale.id),
          label: saleName(sale),
        })),
        translate("marketing.common.unassigned"),
      )}
      <Field id={field("brief")} label={translate("marketing.piece.brief")}>
        <Textarea
          id={field("brief")}
          value={values.brief}
          onChange={(event) => set("brief")(event.target.value)}
          rows={4}
        />
      </Field>
      <Field
        id={field("url")}
        label={translate("marketing.piece.published_url")}
      >
        <Input
          id={field("url")}
          type="url"
          inputMode="url"
          value={values.published_url}
          onChange={(event) => set("published_url")(event.target.value)}
        />
      </Field>
    </FormDialog>
  );
};
